import { authApi } from "#/api/authApi";
import PasswordStrengthInput from "#/components/inputs/PasswordStrengthInput";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import { useAuth } from "#/hook/useAuth";
import { useClient } from "#/hook/useClient";
import { SupportedAuthenticationMethod } from "#/types/client";
import {
  Avatar,
  Button,
  Modal,
  PasswordInput,
  Popover,
  Text,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconLockPassword, IconLogout } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { logger } from "#/utils/logger";

const UserMenu = () => {
  const { t } = useTranslation();
  const { client } = useClient();
  const { logout, user } = useAuth();

  const [
    resetPasswordModalOpened,
    {
      open: openResetPasswordModal,
      close: closeResetPasswordModal,
      toggle: toggleResetPasswordModal,
    },
  ] = useDisclosure(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<string>();

  const form = useForm({
    initialValues: {
      requirementsMet: false,
      newPassword: "",
      repeatPassword: "",
    },
    validate: {
      requirementsMet: (value) => {
        return value ? null : t("auth.passwordValidation.doesNotMeetRequirements");
      },
      newPassword: (value) => {
        if (value.length < 8) {
          return t("auth.passwordValidation.minLength");
        }
        return null;
      },
      repeatPassword: (value, values) => {
        if (value !== values.newPassword) {
          return t("auth.passwordValidation.passwordsDoNotMatch");
        }
        return null;
      },
    },
  });

  const handleResetPassword = async () => {
    if (!user?.id) return;
    const result = form.validate();
    if (result.hasErrors) {
      const errorMessages = Object.values(result.errors)
        .filter((msg): msg is string => typeof msg === "string")
        .join(", ");

      setErrors(errorMessages);
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.changePassword({
        newPassword: form.values.newPassword,
      });

      if (res.success) {
        await logout();
        setLoading(false);
        showSuccessNotification({
          message: t("auth.changePassword.passwordChangedSuccess"),
        });
        closeResetPasswordModal();
      } else {
        throw new Error(res.message);
      }
    } catch (err) {
      logger.error("UserMenu change password error:", err);
      setLoading(false);
      showErrorNotification({
        message: t("auth.changePassword.passwordChangeFailed"),
      });
    }
  };

  return (
    <Popover width={275} position="bottom" withArrow={true} shadow="lg">
      <Popover.Target>
        <Avatar
          radius="xl"
          variant="gradient"
          gradient={{
            from: user?.client?.color || "orange",
            to: "teal.5",
            deg: 45,
          }}
          name={user?.name}
          className="cursor-pointer scale-90 hover:scale-110 transition-all duration-200 shadow-2xl shadow-black.59"
        />
      </Popover.Target>
      <Popover.Dropdown className="max-h-96 overflow-y-scroll p-0">
        <div className="w-full flex flex-col">
          <div className="flex items-center gap-2 bg-gradient-to-br from-slate-100 to-gray-50 border-b border-gray-200 px-4 py-6">
            <Avatar
              radius="xl"
              variant="gradient"
              gradient={{
                from: user?.client?.color || "orange",
                to: "teal.5",
                deg: 45,
              }}
              name={user?.name}
            />

            <div>
              <Title size="sm">{user?.name}</Title>
              <Text size="xs" color="gray.6">
                {user?.email}
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-2 p-4">
            {client?.supportedAuthenticationMethods !==
              SupportedAuthenticationMethod.SSO && (
              <Button
                color="blue.5"
                size="xs"
                variant="outline"
                onClick={openResetPasswordModal}
                leftSection={<IconLockPassword size={16} />}
              >
                {t("auth.changePassword.title")}
              </Button>
            )}
            <Button
              color="red.5"
              size="xs"
              variant="light"
              onClick={logout}
              leftSection={<IconLogout size={16} />}
            >
              {t("auth.logout")}
            </Button>
          </div>
        </div>
      </Popover.Dropdown>

      <Modal
        opened={resetPasswordModalOpened}
        onToggle={toggleResetPasswordModal}
        onClose={() => {
          closeResetPasswordModal();
          setErrors(undefined);
          form.reset();
          form.clearErrors();
        }}
        closeOnEscape={false}
        title={t("auth.changePassword.title")}
        centered
      >
        <div className="w-full flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <PasswordStrengthInput
              label={t("auth.changePassword.newPassword")}
              placeholder={t("auth.changePassword.newPasswordPlaceholder")}
              onPasswordChange={(data) => {
                form.clearErrors();
                setErrors(undefined);
                form.setValues({
                  ...form.values,
                  newPassword: data.value,
                  requirementsMet: data.requirementsMet,
                });
              }}
            />
            <PasswordInput
              placeholder={t("auth.changePassword.repeatPasswordPlaceholder")}
              withAsterisk
              required
              label={t("auth.changePassword.repeatPassword")}
              onChange={(val) => {
                setErrors(undefined);
                form.clearErrors();
                form.setValues({
                  ...form.values,
                  repeatPassword: val.target.value,
                });
              }}
            />

            {errors && (
              <Text color="red.5" size="xs">
                {errors}
              </Text>
            )}
          </div>
          <Button
            color="black"
            size="sm"
            type="submit"
            onClick={() => void handleResetPassword()}
            loading={loading}
          >
            {t("auth.changePassword.changePasswordAndLogin")}
          </Button>
        </div>
      </Modal>
    </Popover>
  );
};

export default UserMenu;
